import React, {useCallback, useMemo, useRef, useState} from 'react';
import {FaEraser, FaPrint, FaSearch} from "react-icons/fa";
import projectUtils from "../../../../utils/project-utils.js";
import {useGlobalKey} from "../../../../hooks/use-global-key.js";
import {Template} from "../../../../components/template/template.jsx";
import HeaderTemplateButton from "../../../../components/button/header-template-button.jsx";
import Button from "../../../../components/button/button.jsx";
import {Card} from "../../../../components/card/card.jsx";
import SentryRow from "../../../../components/template/sentry-row.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import Alert from "../../../../components/alert/alert.jsx";
import {MaskDefault} from "../../../../utils/mask.js";
import CeleparRenavamContent from "./components/celepar-renavam-content.jsx";
import celeparService from "../../../../services/web/celepar-service.js";

const initialState = {
    type: "",
    value: ""
}

const CeleparRenavam = () => {
    const [mask, setMask] = useState(null);
    const [state, setState] = useState(initialState);
    const [filters, setFilters] = useState({});

    const [isLoading, setIsLoading] = useState(false);
    const [headerSelected, setHeaderSelected] = useState(-1);
    const [data, setData] = useState(undefined);

    const printRef = useRef(undefined);

    const types = useMemo(() => [
        {value: "chassi", label: "Chassi"},
        {value: "plate", label: "Placa"},
        {value: "renavam", label: "RENAVAM"}
    ], []);

    const apply = useCallback(() => {
        const type = state.type;
        const value = state.value;

        if (!type) {
            projectUtils.swalErrorMessage("", "O campo \"Tipo\" é obrigatório.");
            return;
        }

        if (!value) {
            projectUtils.swalErrorMessage("", "O campo \"Valor\" é obrigatório.");
            return;
        }

        setIsLoading(true);

        celeparService.renavam({ field: type?.value, value })
            .then(({data}) => {
                setFilters({ type, value });
                setData(data)
            })
            .catch(error => {
                console.error(error);
                let message = 'Erro ao fazer pesquisa.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }

                projectUtils.swalErrorMessage("", message);
            })
            .finally(() => setIsLoading(false));
    }, [state]);

    const clear = useCallback(() => {
        setState(initialState);
        setData(null);
    }, []);

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clear);

    const handleSelected = (selected, set = true) => {
        if (set) setState(prev => ({...prev, type: selected}));

        if (!selected?.value) {
            setMask(null);
            return;
        }

        switch (selected?.value) {
            case 'plate':
                setMask(MaskDefault.plate);
                break
            case 'renavam':
                setMask(MaskDefault.renavam);
                break;
            default:
                setMask(null);
        }
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Pesquisa de RENAVAM</Template.Title>

                {
                    !!data &&
                    <div className={"flex gap-2"}>
                        <Button title="Imprimir" onClick={() => printRef?.current.print()}>
                            <FaPrint />
                        </Button>
                        <HeaderTemplateButton selected={headerSelected} setSelected={setHeaderSelected}/>
                    </div>
                }
            </Template.Header>

            <Template.Body className={"space-y-2"}>
                <Card>
                    <Card.Body>
                        <SentryRow>
                            <SelectComponent
                                id={"type"}
                                value={state.type}
                                options={types}
                                label={"Tipo *"}
                                classNameWrapper={"col-span-6"}
                                onChange={handleSelected}
                            />

                            <div className="col-span-6 flex justify-between gap-4">
                                <InputForm
                                    classNameWrapper={"w-full"}
                                    id={"value"}
                                    label={"Valor *"}
                                    value={state.value}
                                    onChange={({target: {value}}) => setState(prev => ({...prev, value: value}))}
                                    mask={mask}
                                />

                                <div className={"flex gap-2"}>
                                    <Button target={"default"} title={"Limpar"} onClick={clear}>
                                        <FaEraser />
                                    </Button>
                                    <Button title={"Buscar"} onClick={apply}>
                                        <FaSearch />
                                    </Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body>
                        {
                            data === undefined &&
                            <Alert className={"text-center font-bold !text-[14px]"} target={"info"}>
                                Aplique os filtros para realizar a pesquisa.
                            </Alert>
                        }

                        {
                            data &&
                            <CeleparRenavamContent
                                filters={filters}
                                data={data}
                                headerSelected={headerSelected}
                                printRef={printRef}
                            />
                        }
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default CeleparRenavam;